import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Truck, Save } from "lucide-react";
import { API } from "./shared";

const ZONES = [["same_city", "Same city"], ["same_state", "Same state"], ["rest_india", "Rest of India"], ["ne_jk", "North-East & J&K"]];
const SLABS = ["Up to 5 kg (₹ flat)", "5–25 kg (₹ flat)", "25–100 kg (₹ flat)", "Above 100 kg (₹ per kg)"];
const blank = () => ({ dispatch_pincode: "", min_charge_inr: 0, slabs: SLABS.map(() => ({ same_city: "", same_state: "", rest_india: "", ne_jk: "" })) });
const cell = { border: "1px solid #CBD5E1", borderRadius: 7, padding: "7px 8px", fontSize: 13, width: "100%", minWidth: 70, boxSizing: "border-box", fontFamily: "inherit" };

export function DeliveryRateCard() {
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/suppliers/me/rate-card`).then(r => setCard(r.data.card || blank())).catch(() => setCard(blank())); }, []);
  if (!card) return null;
  const setRate = (i, z, v) => setCard(c => ({ ...c, slabs: c.slabs.map((s, j) => (j === i ? { ...s, [z]: v } : s)) }));
  const valid = /^\d{6}$/.test(card.dispatch_pincode) && card.slabs.every(s => ZONES.every(([z]) => s[z] !== "" && Number(s[z]) >= 0));
  const save = async () => {
    setBusy(true);
    try {
      const body = { dispatch_pincode: card.dispatch_pincode, min_charge_inr: Number(card.min_charge_inr) || 0,
        slabs: card.slabs.map(s => Object.fromEntries(ZONES.map(([z]) => [z, Number(s[z])]))) };
      const { data } = await axios.put(`${API}/suppliers/me/rate-card`, body);
      setCard(data.card);
      toast.success("Delivery rate card saved");
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Check the rate card values"); } finally { setBusy(false); }
  };
  return (
    <section className="content" data-testid="delivery-rate-card" style={{ paddingTop: 0 }}>
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, padding: "20px 22px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Truck size={18} style={{ color: "#1d4ed8" }}/><h3 style={{ margin: 0, fontSize: 17 }}>Delivery Rate Card (own courier)</h3></div>
        <p style={{ fontSize: 13, color: "#64748b", margin: "6px 0 16px", maxWidth: 760 }}>
          When you bid with your own courier, we use this card to suggest the delivery charge from the shipment weight and the buyer's pincode zone. You can still edit it on each bid. Once you bid, the charge is locked because the buyer pays upfront.
        </p>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: "#475569", display: "grid", gap: 4 }}>Dispatch pincode *
            <input data-testid="rate-card-pincode" value={card.dispatch_pincode} inputMode="numeric" maxLength={6} style={{ ...cell, width: 140 }}
              onChange={e => setCard({ ...card, dispatch_pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="400001"/>
          </label>
          <label style={{ fontSize: 12, color: "#475569", display: "grid", gap: 4 }}>Minimum charge (₹)
            <input data-testid="rate-card-min" type="number" min="0" value={card.min_charge_inr} style={{ ...cell, width: 140 }} onChange={e => setCard({ ...card, min_charge_inr: e.target.value })}/>
          </label>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table data-testid="rate-card-table" style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 6px", fontSize: 12.5 }}>
            <thead><tr>{["Weight slab", ...ZONES.map(z => z[1])].map(h => <th key={h} style={{ textAlign: "left", fontSize: 11, color: "#64748b", fontWeight: 600, padding: "0 6px" }}>{h}</th>)}</tr></thead>
            <tbody>{SLABS.map((label, i) => (
              <tr key={label}>
                <td style={{ padding: "0 6px", fontWeight: 600, whiteSpace: "nowrap" }}>{label}</td>
                {ZONES.map(([z]) => (
                  <td key={z} style={{ padding: "0 6px" }}>
                    <input data-testid={`rate-card-${i}-${z}`} type="number" min="0" step="0.01" value={card.slabs[i][z]} onChange={e => setRate(i, z, e.target.value)} style={cell}/>
                  </td>
                ))}
              </tr>))}
            </tbody>
          </table>
        </div>
        <button data-testid="rate-card-save" className="primary-btn" style={{ marginTop: 14 }} disabled={busy || !valid} onClick={save}>
          <Save size={14}/> {busy ? "Saving…" : "Save rate card"}
        </button>
      </div>
    </section>
  );
}
